import * as THREE from 'three';
import { FullscreenPass, makeTarget } from '../core/Fullscreen';
import { commonGLSL } from '../shaders/common';
import {
  atmosphereGLSL,
  multiScatterFrag,
  skyIrrFrag,
  skyViewFrag,
  skyViewSampleGLSL,
  transmittanceFrag,
} from '../shaders/atmosphere';

export const GROUND_RADIUS_MM = 6.360;
export const ATMO_RADIUS_MM = 6.460;

/** Solar irradiance at the top of the atmosphere, in scene units (white diffuser facing the sun -> 1.0). */
export const SUN_E_TOA = Math.PI * 1.0;
/** Full-moon irradiance relative to the sun (~1/400k), boosted for perceptual night rendering. */
export const MOON_E_REL = 2.6e-6 * 18;

export interface AtmosphereParams {
  mieScale: number; // 1 = standard continental haze, Bryce clear day ~0.45
  mieScaleHeightKM: number;
  rayleighScale: number;
  groundAlbedo: THREE.Color;
}

/**
 * Owns the sky LUTs and the per-frame sky lighting texture that every lit material samples.
 */
export class Atmosphere {
  readonly transLUT = makeTarget(256, 64);
  readonly msLUT = makeTarget(32, 32);
  readonly skyViewLUT = makeTarget(256, 128, { wrapS: THREE.RepeatWrapping });
  readonly skyIrr = makeTarget(16, 1, { type: THREE.FloatType, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });

  readonly params: AtmosphereParams = {
    mieScale: 0.45,
    mieScaleHeightKM: 1.2,
    rayleighScale: 1.0,
    groundAlbedo: new THREE.Color(0.32, 0.22, 0.16),
  };

  /** Uniforms shared by every shader that uses the atmosphere functions. */
  readonly uniforms = {
    uGroundRadiusMM: { value: GROUND_RADIUS_MM },
    uAtmoRadiusMM: { value: ATMO_RADIUS_MM },
    uRayleighScat: { value: new THREE.Vector3(5.802, 13.558, 33.1) },
    uMieScat: { value: 3.996 },
    uMieAbs: { value: 0.444 },
    uMieG: { value: 0.8 },
    uOzoneAbs: { value: new THREE.Vector3(0.65, 1.881, 0.085) },
    uGroundAlbedo: { value: new THREE.Vector3(0.3, 0.3, 0.3) },
    uMieScaleHeightKM: { value: 1.2 },
    uTransLUT: { value: this.transLUT.texture as THREE.Texture },
    uMSLUT: { value: this.msLUT.texture as THREE.Texture },
    uSkyViewLUT: { value: this.skyViewLUT.texture as THREE.Texture },
    uSkyIrr: { value: this.skyIrr.texture as THREE.Texture },
    uViewPosMM: { value: new THREE.Vector3(0, GROUND_RADIUS_MM + 0.0024, 0) },
    uSunDirW: { value: new THREE.Vector3(0, 1, 0) },
    uSunE: { value: new THREE.Vector3(SUN_E_TOA, SUN_E_TOA, SUN_E_TOA) },
    uMoonDirW: { value: new THREE.Vector3(0, -1, 0) },
    uMoonE: { value: new THREE.Vector3() },
    uCloudCover: { value: 0 },
    uCloudDarkness: { value: 0 },
    uCamAltM: { value: 2440 },
    uGroundAlbedoLocal: { value: new THREE.Vector3(0.55, 0.33, 0.2) },
  };

  private readonly transPass: FullscreenPass;
  private readonly msPass: FullscreenPass;
  private readonly skyViewPass: FullscreenPass;
  private readonly irrPass: FullscreenPass;
  private lutKey = '';

  constructor() {
    const prefix = commonGLSL + atmosphereGLSL;
    const u = this.uniforms;
    this.transPass = new FullscreenPass(transmittanceFrag, u, {}, prefix);
    this.msPass = new FullscreenPass(multiScatterFrag, u, {}, prefix);
    this.skyViewPass = new FullscreenPass(skyViewFrag, u, {}, prefix);
    this.irrPass = new FullscreenPass(skyIrrFrag, u, {}, prefix + skyViewSampleGLSL);
  }

  /**
   * @param camPos camera world position (y = meters above sea level)
   * @param sunDir unit vector toward the sun, moonDir toward the moon
   * @param moonIllum illuminated fraction of the moon
   */
  update(renderer: THREE.WebGLRenderer, camPos: THREE.Vector3, sunDir: THREE.Vector3, moonDir: THREE.Vector3,
    moonIllum: number, cloudCover: number, cloudDarkness: number) {
    const u = this.uniforms;
    const p = this.params;
    u.uMieScat.value = 3.996 * p.mieScale;
    u.uMieAbs.value = 0.444 * p.mieScale;
    u.uMieScaleHeightKM.value = p.mieScaleHeightKM;
    u.uRayleighScat.value.set(5.802, 13.558, 33.1).multiplyScalar(p.rayleighScale);
    u.uGroundAlbedo.value.set(p.groundAlbedo.r, p.groundAlbedo.g, p.groundAlbedo.b);
    const key = [p.mieScale.toFixed(3), p.mieScaleHeightKM.toFixed(2), p.rayleighScale.toFixed(3), p.groundAlbedo.getHexString()].join('|');
    const prevAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    if (key !== this.lutKey) {
      this.lutKey = key;
      this.transPass.render(renderer, this.transLUT);
      this.msPass.render(renderer, this.msLUT);
    }
    u.uViewPosMM.value.set(0, GROUND_RADIUS_MM + Math.max(camPos.y, 1) * 1e-6, 0);
    u.uCamAltM.value = camPos.y;
    u.uSunDirW.value.copy(sunDir);
    u.uSunE.value.setScalar(SUN_E_TOA);
    u.uMoonDirW.value.copy(moonDir);
    // Moon brightness follows phase (opposition surge roughly ~ illum^1.5)
    const moonE = SUN_E_TOA * MOON_E_REL * Math.pow(Math.max(moonIllum, 0), 1.5);
    u.uMoonE.value.set(moonE * 0.95, moonE, moonE * 1.08);
    u.uCloudCover.value = cloudCover;
    u.uCloudDarkness.value = cloudDarkness;
    this.skyViewPass.render(renderer, this.skyViewLUT);
    this.irrPass.render(renderer, this.skyIrr);
    renderer.setRenderTarget(null);
    renderer.autoClear = prevAutoClear;
  }
}
